import { apiFetch } from './api';

// Account-tied hint balance — spendable on web + iOS. Free 3 on signup,
// +5 lazy-granted per calendar month, +25 per purchased pack.

export interface HintBalance {
  balance: number;
  /// ISO timestamp of the last monthly grant (or null if none yet).
  lastMonthlyGrant: string | null;
  /// True iff this read just applied an owed grant (so the UI can
  /// show a small "+5 hints" toast).
  grantedJustNow: boolean;
}

export async function fetchHintBalance(): Promise<HintBalance | null> {
  const res = await apiFetch('/hints/me/balance');
  if (!res.ok) return null;
  return (await res.json()) as HintBalance;
}

/// Atomically decrement the balance by 1. Returns:
///  - `{ ok: true, balance }` on success
///  - `{ ok: false, reason: 'no_hints' | 'request_failed' }` otherwise
export async function spendHint(): Promise<
  | { ok: true; balance: number }
  | { ok: false; reason: 'no_hints' | 'request_failed' }
> {
  const res = await apiFetch('/hints/use', { method: 'POST' });
  if (res.status === 409) return { ok: false, reason: 'no_hints' };
  if (!res.ok) return { ok: false, reason: 'request_failed' };
  const data = await res.json();
  if (typeof data?.balance !== 'number') return { ok: false, reason: 'request_failed' };
  return { ok: true, balance: data.balance };
}

/// Open a Stripe Checkout session for the 25-hint pack. Returns the
/// hosted URL — caller does `window.location.href = url`.
export async function startHintPackCheckout(): Promise<
  | { ok: true; url: string }
  | { ok: false; error: string }
> {
  const res = await apiFetch('/billing/stripe/create-hint-pack-session', { method: 'POST' });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.url !== 'string') return { ok: false, error: 'no_checkout_url' };
  return { ok: true, url: data.url };
}
